"use client";

import { faCartPlus, faPlus, faTrashAlt, faUserHeadset } from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	ContactButton,
	FileInput,
	Input,
	MultiSelect,
	Select,
	SubmitButton,
	Switch,
	Typography,
	useNotifications,
} from "@tosize/ui";
import { isEqual } from "lodash";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
	Controller,
	type UseFieldArrayReturn,
	useFieldArray,
	useFormContext,
} from "react-hook-form";
import { match } from "ts-pattern";
import { z } from "zod";
import { ShowIfDialogActive } from "~/app/[lang]/command/ActiveDialog";
import { EdgebandInput } from "~/app/[lang]/command/dialogs/import/EdgebandInput";
import { ShowFormField } from "~/app/[lang]/command/dialogs/import/Form";
import {
	type FinishesDataType,
	getFinishesData,
} from "~/app/[lang]/command/dialogs/import/actions";
import {
	type FinishesType,
	type ListImportFormType,
	type SidesType,
	defaultImportSchemaItem,
} from "~/app/[lang]/command/dialogs/import/schema";

const csvSides = ["Long side 1", "Long side 2", "Short side 1", "Short side 2"] as const;

type CSVSidesType = (typeof csvSides)[number];

const csvFinishes = [
	"Rounding off top",
	"Rounding off bottom",
	"Rounding off top and bottom",
	"Beveling top",
	"Beveling bottom",
	"Beveling top and bottom",
	"Edge band matching",
	"Edge band custom",
] as const;

type CSVFinishesType = (typeof csvFinishes)[number];

const csvRowSchema = z.object({
	quantity: z.coerce.number().int().positive(),
	length: z.coerce.number().positive(),
	width: z.coerce.number().positive(),
	label: z.string().optional(),
	sides: z
		.string()
		.optional()
		.transform((s) => {
			const sidesArray = s?.replace(/"/g, "").split(", ") || [];
			return sidesArray.filter((side) => csvSides.includes(side)) as unknown as CSVSidesType[];
		})
		.optional(),
	finish: z
		.string()
		.optional()
		.transform((s) => {
			const match = s?.match(/^(.*) - (\d+)mm$/);
			if (match) {
				const type = match[1] as CSVFinishesType;
				const value = Number(match[2]);

				return { type, value };
			}

			return undefined;
		})
		.optional(),
});

interface ImportFormInputsProps {
	lang: string;
	apiUrl: string;
	children?: ReactNode;
	messages: {
		needHelp: string;
		dragAndDrop: string;
		chooseFile: string;
		row: {
			title: string;
			add: string;
			delete: string;
			submit: string;
		};
		quantity: string;
		length: string;
		width: string;
		label: string;
		sides: {
			title: string;
			choose: string;
			required: string;
			allSelected: string;
		};
		finishes: {
			enabled: string;
			disabled: string;
			title: string;
			choose: string;
			edgeband: string;
		};
	};
}

export function ImportFormInputs(props: ImportFormInputsProps) {
	const { lang, apiUrl, children, messages } = props;
	const form = useFormContext<ListImportFormType>();

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: "items",
		rules: { minLength: 1 },
	});

	const groupedProductId = form.watch("grouped_product_id");
	const simpleProductId = form.watch("simple_product_id");
	const productionType = form.watch("production_type");

	const hasProduct = !!groupedProductId && !!simpleProductId && !!productionType;

	const { finishes, isLoading } = getFinishesData({ apiUrl, simpleProductId });
	const hasFinishes =
		!isLoading && !!finishes?.availableOptions.length && !!finishes?.availableSides.length;

	return (
		<>
			{hasProduct && !hasFinishes && (
				<Typography color="text-shades-dark" className="mb-4 text-sm">
					{messages.finishes.disabled}
				</Typography>
			)}

			<ShowIfDialogActive dialog="importProducts_file">
				<ImportFileInput
					lang={lang}
					messages={{
						chooseFile: messages.chooseFile,
						dragAndDrop: messages.dragAndDrop,
						row: messages.row,
					}}
					fields={fields}
					append={append}
					remove={remove}
				/>
			</ShowIfDialogActive>

			{children}

			<div className="flex flex-col">
				{fields.map((item, index) => (
					<div
						key={item.id}
						className="-mx-4 relative flex flex-row items-start gap-4 rounded p-4 odd:bg-shades-light lg:mx-0"
					>
						<Button
							className="mt-[1.75rem] hidden aspect-square h-[2.625rem] lg:inline"
							color="primary-medium"
							disabled={fields.length <= 1}
							onClick={() => remove(index)}
							size="small"
							starticon={faTrashAlt}
							iconSize="1x"
							title={messages.row.delete}
						/>

						<div className="grid grow grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							<Input
								type="number"
								min={1}
								name={`items.${index}.quantity`}
								label={messages.quantity}
								registerOptions={{ valueAsNumber: true }}
								disabled={!hasProduct}
							/>

							<Input
								type="number"
								min={50}
								name={`items.${index}.length`}
								label={messages.length}
								registerOptions={{ valueAsNumber: true }}
								disabled={!hasProduct}
							/>

							<Input
								type="number"
								min={50}
								name={`items.${index}.width`}
								label={messages.width}
								registerOptions={{ valueAsNumber: true }}
								disabled={!hasProduct}
							/>

							<Input name={`items.${index}.label`} label={messages.label} disabled={!hasProduct} />

							<Switch
								name={`items.${index}.finishesEnabled`}
								id={`items.${index}.finishesEnabled`}
								className="h-[2.625rem] font-bold lg:hidden"
								disabled={hasProduct ? !hasFinishes : true}
								label={messages.finishes.enabled}
								onCheckedChange={(checked) => {
									if (!checked) {
										form.unregister(`items.${index}.finishes.sides`);
										form.unregister(`items.${index}.finishes.type`);
										form.unregister(`items.${index}.finishes.radius`);
										form.unregister(`items.${index}.finishes.depth`);
										form.unregister(`items.${index}.finishes.thickness`);
										form.unregister(`items.${index}.finishes.sku`);
									} else if (finishes?.availableOptions.length === 1) {
										form.setValue(
											`items.${index}.finishes.type`,
											finishes.availableOptions[0]!.code,
										);
									}
								}}
							/>

							{hasFinishes && (
								<ShowFormField name={`items.${index}.finishesEnabled`} values={true}>
									<FinishesFields
										finishes={finishes}
										apiUrl={apiUrl}
										index={index}
										messages={{ finishes: messages.finishes, sides: messages.sides }}
									/>
								</ShowFormField>
							)}

							<Button
								className="col-span-full lg:hidden"
								color="primary-medium"
								disabled={fields.length <= 1}
								onClick={() => remove(index)}
								size="small"
								starticon={faTrashAlt}
								iconSize="1x"
							>
								{messages.row.delete}
							</Button>
						</div>

						<div className="hidden flex-col gap-1 lg:flex">
							<label htmlFor={`items.${index}.finishesEnabled`} className="font-bold">
								{messages.finishes.enabled}
							</label>

							<Switch
								name={`items.${index}.finishesEnabled`}
								id={`items.${index}.finishesEnabled`}
								className="h-[2.625rem]"
								disabled={hasProduct ? !hasFinishes : true}
								onCheckedChange={(checked) => {
									if (!checked) {
										form.unregister(`items.${index}.finishes.sides`);
										form.unregister(`items.${index}.finishes.type`);
										form.unregister(`items.${index}.finishes.radius`);
										form.unregister(`items.${index}.finishes.depth`);
										form.unregister(`items.${index}.finishes.thickness`);
										form.unregister(`items.${index}.finishes.sku`);
									} else if (finishes?.availableOptions.length === 1) {
										form.setValue(
											`items.${index}.finishes.type`,
											finishes.availableOptions[0]!.code,
										);
									}
								}}
							/>
						</div>
					</div>
				))}
			</div>

			<div className="-mx-4 sticky bottom-0 mt-auto grid grid-cols-1 xs:grid-cols-2 gap-1 bg-shades-light p-4 xs:last:*:col-span-2 sm:flex sm:justify-end sm:gap-4 sm:[&>*:nth-child(2)]:ml-auto">
				<ContactButton variant="outlined" icon={faUserHeadset}>
					{messages.needHelp}
				</ContactButton>
				<Button
					disabled={!hasProduct}
					onClick={() => append(defaultImportSchemaItem)}
					starticon={faPlus}
				>
					{messages.row.add}
				</Button>
				<SubmitButton color="success" starticon={faCartPlus}>
					{messages.row.submit} {fields.length > 1 ? `(${fields.length})` : undefined}
				</SubmitButton>
			</div>
		</>
	);
}

interface FinishesFieldsProps {
	apiUrl: string;
	index: number;
	messages: Pick<ImportFormInputsProps["messages"], "finishes" | "sides">;
	finishes: NonNullable<FinishesDataType["finishes"]>;
}

function FinishesFields(props: FinishesFieldsProps) {
	const { apiUrl, index, messages, finishes } = props;

	const form = useFormContext<ListImportFormType>();
	const finishType = form.watch(`items.${index}.finishes.type`);

	const getFinsihOptions = useCallback(
		(codes: string[]) => {
			return codes.includes(finishType)
				? finishes?.availableOptions.find(({ code }) => codes.includes(code))
				: undefined;
		},
		[finishType, finishes?.availableOptions],
	);

	const radiusFinish = getFinsihOptions(["afronden_up", "afronden_down", "afronden_both"]);
	const depthFinish = getFinsihOptions(["afschuinen_up", "afschuinen_down", "afschuinen_both"]);
	const matchingFinish = getFinsihOptions(["matching"]);

	const options = finishes.availableSides.map((side) => ({ value: side.code, label: side.label }));

	return (
		<>
			<Select name={`items.${index}.finishes.type`} label={messages.finishes.title}>
				<option value="">{messages.finishes.choose}</option>

				{finishes.availableOptions.map((option) => (
					<option key={option.code} value={option.code}>
						{option.label}
					</option>
				))}
			</Select>

			<ShowFormField name={`items.${index}.finishes.type`} values={["matching", "custom"]}>
				<AllSidesInput index={index} finishes={finishes} messages={{ sides: messages.sides }} />
			</ShowFormField>

			<ShowFormField inversed name={`items.${index}.finishes.type`} values={["matching", "custom"]}>
				<Controller
					control={form.control}
					name={`items.${index}.finishes.sides`}
					disabled={!finishType}
					render={({ field: { value, name, disabled, ref, onChange, onBlur } }) => (
						<MultiSelect
							label={messages.sides.title}
							value={options.filter((c) => value?.includes(c.value))}
							name={name}
							isDisabled={disabled}
							ref={ref}
							onChange={(val) => onChange(val ? val.map((c) => c.value) : [])}
							onBlur={onBlur}
							options={options}
							placeholder={messages.sides.choose}
							wrapperClassName="sm:col-span-2"
						/>
					)}
				/>
			</ShowFormField>

			{!!radiusFinish?.field?.values?.length && (
				<Select
					name={`items.${index}.finishes.radius`}
					label={radiusFinish.field.label}
					registerOptions={{ valueAsNumber: true, shouldUnregister: true }}
				>
					{radiusFinish.field.values.map((fieldValue) => (
						<option key={fieldValue.value} value={fieldValue.value}>
							{fieldValue.label}
						</option>
					))}
				</Select>
			)}

			{!!depthFinish?.field?.values?.length && (
				<Select
					name={`items.${index}.finishes.depth`}
					label={depthFinish.field.label}
					registerOptions={{ valueAsNumber: true, shouldUnregister: true }}
				>
					{depthFinish.field.values.map((fieldValue) => (
						<option key={fieldValue.value} value={fieldValue.value}>
							{fieldValue.label}
						</option>
					))}
				</Select>
			)}

			{!!matchingFinish?.field?.values?.length && (
				<Select
					name={`items.${index}.finishes.thickness`}
					label={matchingFinish.field.label}
					registerOptions={{ valueAsNumber: true, shouldUnregister: true }}
				>
					{matchingFinish.field.values.map((fieldValue) => (
						<option key={fieldValue.value} value={fieldValue.value}>
							{fieldValue.label}
						</option>
					))}
				</Select>
			)}

			<ShowFormField name={`items.${index}.finishes.type`} values={["custom"]}>
				<EdgebandInput
					apiUrl={apiUrl}
					index={index}
					messages={{ label: messages.finishes.edgeband }}
				/>
			</ShowFormField>
		</>
	);
}

interface AllSidesInputProps {
	index: number;
	finishes: NonNullable<FinishesDataType["finishes"]>;
	messages: Pick<ImportFormInputsProps["messages"], "sides">;
}

function AllSidesInput({ index, finishes, messages }: AllSidesInputProps) {
	const form = useFormContext<ListImportFormType>();
	const sides = form.watch(`items.${index}.finishes.sides`);
	const allSideCodes = finishes.availableSides.map((side) => side.code);

	useEffect(() => {
		if (!isEqual(sides, allSideCodes)) {
			form.setValue(`items.${index}.finishes.sides`, allSideCodes);
		}
	}, [sides, allSideCodes, index, form]);

	return (
		<>
			<input type="hidden" {...form.register(`items.${index}.finishes.sides`)} />

			<div className="flex flex-col">
				<Typography variant="paragraph-bold" className="mb-1">
					{messages.sides.title} ({messages.sides.required})
				</Typography>

				<div className="form-input w-full rounded border-shades bg-white opacity-80">
					{messages.sides.allSelected}
				</div>
			</div>
		</>
	);
}

interface ImportFileInputProps
	extends Pick<UseFieldArrayReturn<ListImportFormType>, "fields" | "append" | "remove"> {
	lang: string;
	messages: Pick<ImportFormInputsProps["messages"], "dragAndDrop" | "chooseFile" | "row">;
}

function ImportFileInput({ lang, messages, fields, append, remove }: ImportFileInputProps) {
	const form = useFormContext<ListImportFormType>();
	const { notify } = useNotifications();
	const [errors, setErrors] = useState<Record<number, z.ZodIssue[]>>();

	return (
		<>
			<FileInput
				lang={lang}
				messages={{
					dragAndDrop: messages.dragAndDrop,
					chooseFile: messages.chooseFile,
				}}
				accept=".csv"
				multiple={false}
				onChange={(files) => {
					if (!files?.length) return;

					const reader = new FileReader();
					reader.onload = (e) => {
						if (typeof e.target?.result !== "string") return;
						try {
							const data = e.target.result
								// Split file content into rows
								.split("\n")
								// Split by comma but respect quoted values
								.map((row) => row.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/))
								// Trim spaces and remove empty cells
								.map((row) => row.map((cell) => cell.trim()).filter(Boolean))
								// Remove header
								.filter((_row, index) => index !== 0)
								// Validate and transform rows
								.map(([quantity, length, width, label, sides, finish]) =>
									csvRowSchema.safeParse({ quantity, length, width, label, sides, finish }),
								);

							for (const index of fields.keys()) {
								remove(index);
							}

							for (const [index, row] of data.entries()) {
								if (!row.success) {
									setErrors((prev) => ({ ...prev, [index + 1]: row.error.issues }));
									continue;
								}

								const { quantity, length, width, label, sides, finish } = row.data;

								const finishesEnabled = !!sides?.length && !!finish;
								let finishes: ListImportFormType["items"][number]["finishes"] = undefined;

								if (finishesEnabled) {
									const finishSides = sides.map((side) =>
										match(side)
											.returnType<SidesType>()
											.with("Long side 1", () => "length1")
											.with("Long side 2", () => "length2")
											.with("Short side 1", () => "width1")
											.with("Short side 2", () => "width2")
											.exhaustive(),
									);

									const finishType = match(finish)
										.returnType<FinishesType>()
										.with({ type: "Rounding off top" }, () => "afronden_up")
										.with({ type: "Rounding off bottom" }, () => "afronden_down")
										.with({ type: "Rounding off top and bottom" }, () => "afronden_both")
										.with({ type: "Beveling top" }, () => "afschuinen_up")
										.with({ type: "Beveling bottom" }, () => "afschuinen_down")
										.with({ type: "Beveling top and bottom" }, () => "afschuinen_both")
										.with({ type: "Edge band matching" }, () => "matching")
										.with({ type: "Edge band custom" }, () => "custom")
										.exhaustive();

									const value = match(finish)
										.with(
											{ type: "Rounding off top" },
											{ type: "Rounding off bottom" },
											{ type: "Rounding off top and bottom" },
											(t) => ({ radius: t.value }),
										)
										.with(
											{ type: "Beveling top" },
											{ type: "Beveling bottom" },
											{ type: "Beveling top and bottom" },
											(t) => ({ depth: t.value }),
										)
										.with({ type: "Edge band matching" }, (t) => ({ thickness: t.value }))
										.otherwise(() => undefined);

									finishes = { sides: finishSides, type: finishType, ...value };
								}

								append({
									quantity: quantity ?? null,
									length: length ?? null,
									width: width ?? null,
									label,
									finishesEnabled,
									finishes,
								});
							}

							form.trigger();
						} catch (error) {
							if (error instanceof z.ZodError) {
								setErrors((prev) => ({
									...prev,
									[prev ? Object.keys(prev).length + 1 : 0]: error.issues,
								}));
							}

							if (error instanceof Error) {
								notify({ variant: "danger", title: error.message });
							}

							console.error(error);
						}
					};

					for (const file of files) {
						reader.readAsText(file);
					}
				}}
			/>
			{!!errors && (
				<ul className="mt-1 ml-6 list-outside list-disc text-danger text-sm">
					{Object.entries(errors).map(([key, issues]) => {
						const position = Number(key) + 1;

						return (
							<li key={position}>
								<span>
									{messages.row.title}{" "}
									<span className="font-mono">
										{Number(position) < 10 ? `0${position}` : position}
									</span>
								</span>

								{issues.length > 1 ? (
									<ul className="ml-[1.125rem] list-outside list-disc">
										{issues.map((issue, index) => (
											<li key={index}>
												{issue.path} - {issue.code}: {issue.message}
											</li>
										))}
									</ul>
								) : (
									issues.map((issue, index) => (
										<span key={index}>{` ${issue.path} - ${issue.code}: ${issue.message}`}</span>
									))
								)}
							</li>
						);
					})}
				</ul>
			)}
		</>
	);
}
